/**
 * SharePoint-style card details pane: this-period value, comments, history, audit,
 * with card settings behind a gear.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaretLeftIcon, CaretRightIcon, GearIcon } from '@phosphor-icons/react';
import SidePanel from 'src/components/ui/SidePanel';
import Button from 'src/components/ui/Button';
import { ConfirmModal } from 'src/components/ui/ConfirmModal';
import { InputField } from 'src/components/ui/Input';
import Loading from 'src/components/ui/Loading';
import { useDataValues, useMetricNode } from '../api/hooks';
import {
  formatPeriodLabel,
  getFiscalQuarterFromMonth,
  representativeMonthForQuarter,
  type PeriodGrain
} from '../utils/constants';
import { isoYearForCalendarMonth } from '../utils/isoWeek';
import type { AssuranceLevel } from '../utils/color';
import type { CardChartType } from '../api/types';
import { AssuranceLevelPill } from './MetricTileCharts';
import NodeCommentPopup from './NodeCommentPopup';
import NodeDetailModal from './NodeDetailModal';
import { ResourcePermissionRole } from 'src/features/chat/types';

export type CardDetailsTab = 'history' | 'comments' | 'audit';

type NodeKind = 'data_card' | 'group_card' | 'status_card';
type Cadence = 'monthly' | 'quarterly' | 'weekly' | null;

interface CardDetailsPanelProps {
  open: boolean;
  onClose: () => void;
  nodeId: string;
  scopeId: string;
  nodeName: string;
  nodeType: NodeKind;
  updateCadence: Cadence;
  currentValue: number | null;
  assurance: AssuranceLevel;
  suffix: string;
  year: number;
  month: number;
  week: number;
  grain: PeriodGrain;
  canEdit: boolean;
  canEditData: boolean;
  canComment: boolean;
  initialTab?: CardDetailsTab;
  groupChildren?: Array<{
    id: string;
    title: string;
    type: NodeKind;
    groupNumericDisplayValue?: number | null;
  }>;
  siblingIds: string[];
  onDeleted?: () => void;
  onSaved?: () => void;
  onOpenNode: (nodeId: string, tab?: CardDetailsTab, settingsTab?: string) => void;
  /** Jump to the dashboard itself and open its Reminders settings. */
  onOpenDashboardReminders?: () => void;
  /** Open directly on a settings tab (e.g. Reminders) instead of the main pane. */
  initialSettingsTab?: string;
  onDraftChartTypeChange?: (chartType: CardChartType | null) => void;
  /** Dock beside the dashboard (which shifts left) instead of overlaying it. */
  docked?: boolean;
}

/** Panel width in px; the dashboard reserves this much space when the panel is docked. */
export const CARD_DETAILS_PANEL_WIDTH = 520;

function periodSlot(cadence: Cadence, year: number, month: number, week: number) {
  if (cadence === 'weekly') {
    return {
      period_year: isoYearForCalendarMonth(year, month, week),
      period_month: null as number | null,
      period_week: week
    };
  }
  if (cadence === 'quarterly') {
    return {
      period_year: year,
      period_month: representativeMonthForQuarter(getFiscalQuarterFromMonth(month)),
      period_week: null as number | null
    };
  }
  return { period_year: year, period_month: month, period_week: null as number | null };
}

const DATA_TABS: { id: CardDetailsTab; label: string }[] = [
  { id: 'history', label: 'History' },
  { id: 'comments', label: 'Comments' },
  { id: 'audit', label: 'Audit' }
];

const GROUP_TABS: { id: CardDetailsTab; label: string }[] = [{ id: 'comments', label: 'Comments' }];

export default function CardDetailsPanel({
  open,
  onClose,
  nodeId,
  scopeId,
  nodeName,
  nodeType,
  updateCadence,
  currentValue,
  assurance,
  suffix,
  year,
  month,
  week,
  grain,
  canEdit,
  canEditData,
  canComment,
  initialTab,
  groupChildren = [],
  siblingIds,
  onDeleted,
  onSaved,
  onOpenNode,
  onOpenDashboardReminders,
  initialSettingsTab,
  onDraftChartTypeChange,
  docked = false
}: CardDetailsPanelProps) {
  const { data: node, isLoading: nodeLoading } = useMetricNode(nodeId);
  const { data: dataValues } = useDataValues(nodeId);

  const cadence = (node?.update_cadence ?? updateCadence ?? 'monthly') as Cadence;
  const isGroup = (node?.type ?? nodeType) === 'group_card';
  const isStatus = (node?.type ?? nodeType) === 'status_card';
  const hasPeriodValue = !isGroup;
  const title = node?.title ?? nodeName;
  const periodLabel = formatPeriodLabel(year, month, cadence, week);
  const isOwner = node?.user_permission === ResourcePermissionRole.OWNER;
  const tabs = isGroup ? GROUP_TABS : isOwner ? DATA_TABS : DATA_TABS.filter((item) => item.id !== 'audit');
  const defaultTab: CardDetailsTab = isGroup ? 'comments' : 'history';
  const resolvedInitial = initialTab && tabs.some((item) => item.id === initialTab) ? initialTab : defaultTab;

  const [tab, setTab] = useState<CardDetailsTab>(resolvedInitial);
  const [view, setView] = useState<'main' | 'settings'>(initialSettingsTab ? 'settings' : 'main');
  const [draft, setDraft] = useState('');
  const [actionsFooterEl, setActionsFooterEl] = useState<HTMLDivElement | null>(null);
  const requestCloseSettingsRef = useRef<() => void>(() => setView('main'));
  const requestClosePanelRef = useRef<() => void>(onClose);
  const requestCloseHistoryPanelRef = useRef<() => void>(onClose);
  const pendingAfterPeriodDiscardRef = useRef<(() => void) | null>(null);
  const [showPeriodDiscardConfirm, setShowPeriodDiscardConfirm] = useState(false);

  const slot = useMemo(() => periodSlot(cadence, year, month, week), [cadence, year, month, week]);

  const savedSlotValue = useMemo(() => {
    const rows = dataValues?.data_values ?? [];
    const match = rows.find(
      (row) =>
        row.period_year === slot.period_year &&
        (row.period_month ?? null) === slot.period_month &&
        (row.period_week ?? null) === slot.period_week
    );
    return match?.value ?? null;
  }, [dataValues, slot]);

  const displayValue = savedSlotValue ?? currentValue;

  useEffect(() => {
    if (!open) return;
    setTab(resolvedInitial);
    setView(initialSettingsTab ? 'settings' : 'main');
  }, [open, nodeId, resolvedInitial, initialSettingsTab]);

  useEffect(() => {
    setDraft(displayValue == null ? '' : String(displayValue));
  }, [displayValue, nodeId, open]);

  const dirtyPeriod =
    hasPeriodValue &&
    canEditData &&
    (displayValue == null ? draft.trim() !== '' : draft.trim() !== String(displayValue));

  const discardPeriodThen = useCallback(
    (action: () => void) => {
      if (!dirtyPeriod) {
        action();
        return;
      }
      pendingAfterPeriodDiscardRef.current = action;
      setShowPeriodDiscardConfirm(true);
    },
    [dirtyPeriod]
  );

  const handleConfirmDiscardPeriod = useCallback(() => {
    const action = pendingAfterPeriodDiscardRef.current;
    pendingAfterPeriodDiscardRef.current = null;
    setShowPeriodDiscardConfirm(false);
    setDraft(displayValue == null ? '' : String(displayValue));
    action?.();
  }, [displayValue]);

  const handleCancelDiscardPeriod = useCallback(() => {
    pendingAfterPeriodDiscardRef.current = null;
    setShowPeriodDiscardConfirm(false);
  }, []);

  const handleSidePanelClose = useCallback(() => {
    if (view === 'settings') {
      discardPeriodThen(() => requestClosePanelRef.current());
      return;
    }
    if (tab === 'history' && hasPeriodValue) {
      requestCloseHistoryPanelRef.current();
      return;
    }
    discardPeriodThen(() => onClose());
  }, [view, tab, hasPeriodValue, discardPeriodThen, onClose]);

  // Parsed for the footer Save. Null when the field is unchanged.
  const periodEntry = useMemo(() => {
    if (!dirtyPeriod) return null;
    const trimmed = draft.trim();
    if (trimmed === '' || trimmed === '—') {
      return { ...slot, value: null, error: 'Enter a value to save' };
    }
    if (isStatus) {
      const numeric = trimmed === '1' || trimmed.toLowerCase() === 'yes' ? 1 : 0;
      return { ...slot, value: numeric, error: null };
    }
    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric)) {
      return { ...slot, value: null, error: 'Enter a valid number' };
    }
    return { ...slot, value: numeric, error: null };
  }, [dirtyPeriod, draft, isStatus, slot]);

  const siblingIndex = siblingIds.indexOf(nodeId);

  const prevId = useMemo(() => {
    if (siblingIds.length < 2 || siblingIndex < 0) return null;
    return siblingIds[(siblingIndex - 1 + siblingIds.length) % siblingIds.length];
  }, [siblingIds, siblingIndex]);

  const nextId = useMemo(() => {
    if (siblingIds.length < 2 || siblingIndex < 0) return null;
    return siblingIds[(siblingIndex + 1) % siblingIds.length];
  }, [siblingIds, siblingIndex]);

  const showNav = siblingIndex >= 0 && prevId != null && nextId != null;
  const navTab: CardDetailsTab = isGroup ? 'history' : tab;

  const goPrev = useCallback(() => {
    if (prevId) discardPeriodThen(() => onOpenNode(prevId, navTab));
  }, [prevId, onOpenNode, navTab, discardPeriodThen]);

  const goNext = useCallback(() => {
    if (nextId) discardPeriodThen(() => onOpenNode(nextId, navTab));
  }, [nextId, onOpenNode, navTab, discardPeriodThen]);

  useEffect(() => {
    if (!open || view !== 'main' || !showNav || showPeriodDiscardConfirm) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable="true"]')) {
        return;
      }
      event.preventDefault();
      if (event.key === 'ArrowLeft') goPrev();
      else goNext();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, view, showNav, showPeriodDiscardConfirm, goPrev, goNext]);

  const comments = (
    <NodeCommentPopup
      nodeId={nodeId}
      nodeName={title}
      scopeId={nodeId}
      open={open}
      anchorEl={null}
      onClose={onClose}
      canComment={canComment}
      year={year}
      month={month}
      week={week}
      grain={grain}
      nodeType={node?.type ?? nodeType}
      updateCadence={cadence}
      embedded
      actionsPortal={actionsFooterEl}
    />
  );

  return (
    <>
      <SidePanel
        open={open}
        onClose={handleSidePanelClose}
        maxWidth={CARD_DETAILS_PANEL_WIDTH}
        docked={docked}
        contentClassName="flex flex-col overflow-hidden min-h-0 p-0"
        aria-label={`${title} details`}
        header={
          view === 'settings' ? (
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                icon={CaretLeftIcon}
                aria-label="Back to card details"
                onClick={() => requestCloseSettingsRef.current()}
              />
              <div className="min-w-0">
                <p className="text-base font-medium text-content-default truncate">Card settings</p>
                <p className="text-xs text-content-subtle truncate">{title}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2 min-w-0">
              <div className="min-w-0">
                <p className="text-base font-medium text-content-default truncate">{title}</p>
                <p className="text-xs text-content-subtle">
                  {isGroup ? 'Group' : isStatus ? 'Status' : 'Data'} · {cadence ?? 'monthly'} · {periodLabel}
                </p>
              </div>
              {canEdit && (
                <Button
                  variant="outline"
                  size="md"
                  icon={GearIcon}
                  iconProps={{ size: 20, weight: 'regular' }}
                  aria-label="Card settings"
                  title="Card settings"
                  onClick={() => setView('settings')}
                  className="shrink-0"
                />
              )}
            </div>
          )
        }
        footer={
          view === 'settings' ||
          (view === 'main' && (showNav || (tab === 'history' && canEditData) || (tab === 'comments' && canComment))) ? (
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {view === 'main' && showNav && (
                  <>
                    <Button
                      variant="outline"
                      icon={CaretLeftIcon}
                      title="Previous card (←)"
                      aria-label="Previous card"
                      onClick={goPrev}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      icon={CaretRightIcon}
                      iconPosition="right"
                      title="Next card (→)"
                      aria-label="Next card"
                      onClick={goNext}
                    >
                      Next
                    </Button>
                  </>
                )}
              </div>
              <div ref={setActionsFooterEl} className="flex items-center gap-2" />
            </div>
          ) : undefined
        }
      >
        {nodeLoading && !node ? (
          <div className="flex-1 px-5 py-4">
            <Loading />
          </div>
        ) : view === 'settings' ? (
          <div className="flex-1 min-h-0 overflow-hidden px-5 py-4">
            <NodeDetailModal
              nodeId={nodeId}
              scopeId={scopeId}
              isOpen={open}
              onClose={() => {
                onDraftChartTypeChange?.(null);
                setView('main');
              }}
              onDeleted={onDeleted}
              readOnly={!canEditData}
              dataEntryOnly={canEditData && !canEdit}
              groupChildren={groupChildren}
              chrome="none"
              sections="settings"
              stayOpenOnSave
              actionsPortal={actionsFooterEl}
              initialTabLabel={initialSettingsTab}
              onDraftChartTypeChange={onDraftChartTypeChange}
              viewingPeriod={slot}
              onBindRequestClose={(requestClose) => {
                requestCloseSettingsRef.current = () => {
                  onDraftChartTypeChange?.(null);
                  requestClose();
                };
              }}
              onClosePanel={() => {
                onDraftChartTypeChange?.(null);
                onClose();
              }}
              onBindRequestPanelClose={(requestClose) => {
                requestClosePanelRef.current = () => {
                  onDraftChartTypeChange?.(null);
                  requestClose();
                };
              }}
              onOpenDashboardReminders={scopeId !== nodeId ? onOpenDashboardReminders : undefined}
              onSaved={() => {
                onDraftChartTypeChange?.(null);
                onSaved?.();
                setView('main');
              }}
            />
          </div>
        ) : (
          <div className="flex flex-col h-full min-h-0">
            {hasPeriodValue && (
              <div className="shrink-0 px-5 pt-4">
                <div className="rounded-lg border border-border-default bg-surface-subtle p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-medium text-content-subtle">This period · {periodLabel}</span>
                    {assurance !== 'NaN' && <AssuranceLevelPill level={assurance} />}
                  </div>
                  <p className="text-xs text-content-subtlest mb-2">
                    The value currently shown on the dashboard for the selected reporting period.
                  </p>
                  {isStatus ? (
                    <div className="flex items-center gap-2">
                      {(['1', '0'] as const).map((val) => (
                        <Button
                          key={val}
                          size="sm"
                          variant={draft === val ? 'primary' : 'outline'}
                          disabled={!canEditData}
                          onClick={() => setDraft(val)}
                        >
                          {val === '1' ? 'Yes' : 'No'}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-end gap-2">
                      <div className="flex-1 min-w-0">
                        <InputField
                          type="number"
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          disabled={!canEditData}
                          fullWidth
                          inputClassName="text-lg font-semibold"
                        />
                      </div>
                      {suffix && <span className="text-sm text-content-subtle pb-2">{suffix}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tabs.length > 1 && (
              <div className="flex border-b border-border-default shrink-0 px-5 mt-4">
                {tabs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`flex-1 py-2 text-sm font-medium border-b-2 -mb-px ${
                      tab === item.id
                        ? 'border-secondary-blue text-content-default'
                        : 'border-transparent text-content-subtle hover:text-content-default'
                    }`}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}

            <div className="flex-1 min-h-0 overflow-hidden px-5 py-4 flex flex-col">
              {tab === 'comments' && comments}
              {tab === 'history' && hasPeriodValue && (
                <NodeDetailModal
                  nodeId={nodeId}
                  scopeId={scopeId}
                  isOpen={open}
                  onClose={onClose}
                  readOnly={!canEditData}
                  dataEntryOnly={canEditData && !canEdit}
                  chrome="none"
                  sections="history"
                  hideChromeActions
                  stayOpenOnSave
                  actionsPortal={actionsFooterEl}
                  viewingPeriod={slot}
                  periodEntry={periodEntry}
                  onClosePanel={onClose}
                  onBindRequestPanelClose={(requestClose) => {
                    requestCloseHistoryPanelRef.current = requestClose;
                  }}
                  onSaved={onSaved}
                />
              )}
              {tab === 'audit' && !isGroup && (
                <NodeDetailModal
                  nodeId={nodeId}
                  scopeId={scopeId}
                  isOpen={open}
                  onClose={onClose}
                  chrome="none"
                  sections="audit"
                  hideChromeActions
                  readOnly
                />
              )}
            </div>
          </div>
        )}
      </SidePanel>
      <ConfirmModal
        open={showPeriodDiscardConfirm}
        onClose={handleCancelDiscardPeriod}
        onConfirm={handleConfirmDiscardPeriod}
        title="Unsaved Changes"
        message="The unsaved This-period value will be discarded."
        confirmText="Discard"
        cancelText="Keep Editing"
        variant="warning"
      />
    </>
  );
}
